import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Globe, 
  Search, 
  Loader2,
  CheckCircle,
  Clock,
  Filter,
  BookOpen,
  Play,
  BarChart3,
  Eye,
  EyeOff,
  RefreshCw,
  Upload,
  Plus,
  Pencil,
  Sparkles,
  Trash2
} from 'lucide-react';
import ExternalCourseEditDialog from './ExternalCourseEditDialog';
import ExternalCourseBulkUpload from './ExternalCourseBulkUpload';
import { thumbSrc, thumbnailErrorMessage } from './ExternalCourseThumbnail';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Badge } from '../ui/badge';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

export default function ExternalCoursesAdmin({ token }) {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSpecialisation, setSelectedSpecialisation] = useState('all');
  const [specialisations, setSpecialisations] = useState([]);
  const [stats, setStats] = useState({ total: 0, published: 0, specialisations: 0 });
  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);
  const [editing, setEditing] = useState(undefined); // undefined = closed, null = new, object = edit
  const [showBulk, setShowBulk] = useState(false);

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const [genProgress, setGenProgress] = useState(null); // {done, total}
  const generateMissing = async () => {
    const targets = (selected.length ? courses.filter((c) => selected.includes(c.course_id)) : courses).filter((c) => !c.thumbnail_url);
    if (!targets.length) { toast.info('Every course in this selection already has a thumbnail'); return; }
    if (!window.confirm(`Generate AI thumbnails for ${targets.length} course(s) without one? This uses Universal Key credits.`)) return;
    setGenProgress({ done: 0, total: targets.length });
    let ok = 0;
    for (const c of targets) {
      try {
        await axios.post(`${API}/admin/external-courses/${c.course_id}/generate-thumbnail`, {}, { headers: { Authorization: `Bearer ${token}` } });
        ok += 1;
      } catch (e) {
        toast.error(`${c.title}: ${thumbnailErrorMessage(e)}`);
        if (e.response?.status === 402) break;
      }
      setGenProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setGenProgress(null);
    if (ok > 0) toast.success(`Generated ${ok} thumbnail(s)`);
    fetchCourses();
  };

  const bulkAction = async (payload, label) => {
    if (payload.delete) {
      const n = selected.length;
      const typed = window.prompt(`Permanently delete ${n} external course${n > 1 ? 's' : ''}? Learners will no longer see them.\n\nType DELETE to confirm.`);
      if (typed !== 'DELETE') {
        if (typed !== null) toast.error('Not deleted - you must type DELETE');
        return;
      }
    }
    try {
      const res = await axios.post(`${API}/admin/external-courses/bulk-edit`, { course_ids: selected, ...payload }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${res.data?.deleted ?? res.data?.updated ?? selected.length} course(s) ${label}`);
      setSelected([]);
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk update failed');
    }
  };

  useEffect(() => {
    fetchCourses();
  }, [selectedSpecialisation]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedSpecialisation && selectedSpecialisation !== 'all') {
        params.append('specialisation', selectedSpecialisation);
      }
      
      params.append('limit', '500');
      const res = await axios.get(`${API}/admin/external-courses?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCourses(res.data.courses || []);
      setSpecialisations(res.data.specialisations || []);
      setStats({
        total: res.data.total || 0,
        published: (res.data.courses || []).filter(c => c.status === 'published').length,
        specialisations: (res.data.specialisations || []).length
      });
    } catch (error) {
      console.error('Error fetching external courses:', error);
      toast.error('Failed to load external courses');
    } finally {
      setLoading(false);
    }
  };

  const togglePublish = async (course) => {
    const newStatus = course.status === 'published' ? 'draft' : 'published';
    try {
      await axios.put(`${API}/admin/external-courses/${course.course_id}`, {
        status: newStatus,
        is_published: newStatus === 'published'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Course ${newStatus === 'published' ? 'published' : 'unpublished'}`);
      fetchCourses();
    } catch (error) {
      toast.error('Failed to update course status');
    }
  };

  const filteredCourses = courses.filter(course =>
    course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.specialisation?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.original_category?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getLevelColor = (level) => {
    switch (level?.toLowerCase()) {
      case 'introductory': return 'bg-green-500/20 text-green-400';
      case 'intermediate': return 'bg-yellow-500/20 text-yellow-400';
      case 'advanced': return 'bg-red-500/20 text-red-400';
      default: return 'bg-zinc-500/20 text-zinc-400';
    }
  };

  return (
    <div className="space-y-6" data-testid="external-courses-admin">
      {/* Header Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <BookOpen className="h-5 w-5 text-blue-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white" data-testid="ext-admin-total">{stats.total}</p>
              <p className="text-sm text-zinc-400">Total Courses</p>
            </div>
          </div>
        </div>
        <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white" data-testid="ext-admin-published">{stats.published}</p>
              <p className="text-sm text-zinc-400">Published</p>
            </div>
          </div>
        </div>
        <div className="bg-zinc-800/50 rounded-xl p-4 border border-zinc-700/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-purple-500/20 rounded-lg">
              <BarChart3 className="h-5 w-5 text-purple-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{stats.specialisations}</p>
              <p className="text-sm text-zinc-400">Specialisations</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
          <Input
            placeholder="Search courses..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 bg-zinc-800 border-zinc-700"
            data-testid="ext-admin-search"
          />
        </div>
        <Select value={selectedSpecialisation} onValueChange={setSelectedSpecialisation}>
          <SelectTrigger className="w-[220px] bg-zinc-800 border-zinc-700" data-testid="ext-admin-spec-filter">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="All Specialisations" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Specialisations</SelectItem>
            {specialisations.map(spec => (
              <SelectItem key={spec} value={spec}>{spec}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={fetchCourses} className="border-zinc-700">
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh
        </Button>
        <Button variant="outline" onClick={() => setShowBulk(true)} className="border-zinc-700" data-testid="ext-admin-bulk-upload-btn">
          <Upload className="h-4 w-4 mr-2" />
          Bulk upload
        </Button>
        <Button variant="outline" onClick={generateMissing} disabled={!!genProgress} className="border-zinc-700" data-testid="ext-admin-generate-missing">
          {genProgress ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
          {genProgress ? `Generating ${genProgress.done}/${genProgress.total}…` : 'Generate missing thumbnails'}
        </Button>
        <Button onClick={() => setEditing(null)} className="btn-gold" data-testid="ext-admin-add-btn">
          <Plus className="h-4 w-4 mr-2" />
          Add course
        </Button>
      </div>

      <div className="flex flex-wrap items-center gap-2 p-3 rounded-xl bg-zinc-800/70 border border-zinc-700" data-testid="ext-admin-bulk-bar">
        <span className="text-sm text-white mr-2" data-testid="ext-admin-selected-count">{selected.length} selected</span>
        {selected.length === 0 ? (
          <>
            <span className="text-xs text-zinc-500">Tick courses (or select all shown) to publish, hide or delete them in bulk.</span>
            <Button size="sm" variant="ghost" onClick={() => setSelected(filteredCourses.map((c) => c.course_id))} className="text-zinc-300" data-testid="ext-bulk-select-all">Select all shown ({filteredCourses.length})</Button>
          </>
        ) : (
          <>
            <Button size="sm" onClick={() => bulkAction({ changes: { status: 'published' } }, 'published')} className="bg-green-700 hover:bg-green-600 text-white" data-testid="ext-bulk-publish">Publish</Button>
            <Button size="sm" onClick={() => bulkAction({ changes: { status: 'draft' } }, 'hidden')} className="bg-zinc-700 hover:bg-zinc-600 text-white" data-testid="ext-bulk-unpublish">Hide (draft)</Button>
            <Button size="sm" onClick={() => bulkAction({ delete: true }, 'deleted')} className="bg-red-800 hover:bg-red-700 text-white" data-testid="ext-bulk-delete">
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected(filteredCourses.map((c) => c.course_id))} className="text-zinc-300" data-testid="ext-bulk-select-all">Select all shown ({filteredCourses.length})</Button>
            <Button size="sm" variant="ghost" onClick={() => setSelected([])} className="text-zinc-400" data-testid="ext-bulk-clear">Clear</Button>
          </>
        )}
      </div>

      <ExternalCourseEditDialog open={editing !== undefined} course={editing || null} token={token} onThumbChange={fetchCourses}
        onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); fetchCourses(); }} />
      <ExternalCourseBulkUpload open={showBulk} token={token} onClose={() => setShowBulk(false)}
        onDone={() => { setShowBulk(false); fetchCourses(); }} />

      {/* Course List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
        </div>
      ) : filteredCourses.length === 0 ? (
        <div className="text-center py-12 bg-zinc-800/30 rounded-xl border border-zinc-700/50">
          <Globe className="h-12 w-12 mx-auto text-zinc-600 mb-4" />
          <p className="text-zinc-400">No external courses found</p>
          <p className="text-sm text-zinc-500 mt-1">
            {searchTerm ? 'Try adjusting your search' : 'Run the seed script to add courses'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredCourses.map(course => (
            <div
              key={course.course_id}
              data-testid={`ext-admin-card-${course.course_id}`}
              className="bg-zinc-800/50 rounded-xl border border-zinc-700/50 overflow-hidden hover:border-zinc-600 transition-colors"
            >
              {/* Course Header */}
              <div className="aspect-video bg-zinc-900 overflow-hidden flex items-center justify-center">
                {course.thumbnail_url
                  ? <img src={thumbSrc(course.thumbnail_url)} alt="" className="w-full h-full object-cover" data-testid={`ext-admin-thumb-${course.course_id}`} />
                  : <span className="text-xs text-zinc-600" data-testid={`ext-admin-nothumb-${course.course_id}`}>No thumbnail</span>}
              </div>
              <div className="p-4 border-b border-zinc-700/50">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <input
                    type="checkbox"
                    checked={selected.includes(course.course_id)}
                    onChange={() => toggleSelect(course.course_id)}
                    className="mt-1 h-4 w-4 accent-amber-500 shrink-0"
                    data-testid={`ext-admin-select-${course.course_id}`}
                  />
                  <h3 className="font-semibold text-white line-clamp-2 flex-1">{course.title}</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => togglePublish(course)}
                    data-testid={`ext-admin-toggle-${course.course_id}`}
                    title={course.status === 'published' ? 'Published - click to unpublish' : 'Hidden - click to publish'}
                    className={course.status === 'published' ? 'text-green-400' : 'text-zinc-400'}
                  >
                    {course.status === 'published' ? (
                      <Eye className="h-4 w-4" />
                    ) : (
                      <EyeOff className="h-4 w-4" />
                    )}
                  </Button>
                </div>
                <p className="text-sm text-zinc-400 line-clamp-2">{course.description}</p>
                <p className="text-[11px] text-zinc-600 mt-2 font-mono select-all" data-testid={`ext-admin-id-${course.course_id}`}>{course.course_id}</p>
              </div>

              {/* Course Meta */}
              <div className="p-4 space-y-3">
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline" className="text-xs">
                    {course.specialisation}
                  </Badge>
                  {course.original_category && (
                    <Badge variant="outline" className="text-xs text-zinc-500">
                      {course.original_category}
                    </Badge>
                  )}
                  {course.level && (
                    <Badge className={`text-xs ${getLevelColor(course.level)}`}>
                      {course.level}
                    </Badge>
                  )}
                </div>

                <div className="flex items-center gap-4 text-sm text-zinc-400">
                  {course.estimated_duration && (
                    <span className="flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {course.estimated_duration}
                    </span>
                  )}
                  {course.provider && (
                    <span className="flex items-center gap-1">
                      <Globe className="h-3.5 w-3.5" />
                      {course.provider}
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 border-zinc-700"
                    onClick={() => navigate(`/external-courses/${course.course_id}`)}
                    data-testid={`ext-admin-preview-${course.course_id}`}
                  >
                    <Play className="h-4 w-4 mr-1" />
                    Preview
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1 border-zinc-700"
                    onClick={() => setEditing(course)}
                    data-testid={`ext-admin-edit-${course.course_id}`}
                  >
                    <Pencil className="h-4 w-4 mr-1" />
                    Edit
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <Globe className="h-5 w-5 text-blue-400 mt-0.5" />
          <div>
            <p className="font-medium text-blue-400">Old Mutual External Content Library</p>
            <p className="text-sm text-zinc-400 mt-1">
              These courses are sourced from Old Mutual's compliance training platform. 
              Learners can self-attest completion and earn XP points.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
